import {type FC} from 'react'
import classNames from 'classnames'

import {ColumnSkeleton} from './ColumnSkeleton'

type FixedTableBodySkeletonColumn = {
  dataKey: string
  widthPercentage: number
}

type FixedTableBodySkeletonProps = {
  columns: FixedTableBodySkeletonColumn[]
  numberOfRows?: number
  alignIndexRight?: number[]
  dataCy?: string
}

/**
 * Skeleton for tables that use a flex/block + table-fixed row layout
 * (header/body as display:block, rows as display:table table-fixed).
 * Unlike TableBodySkeleton, this does not add spacer cells or absolute-positioned bars.
 */
export const FixedTableBodySkeleton: FC<FixedTableBodySkeletonProps> = ({
  columns,
  numberOfRows = 5,
  alignIndexRight = [],
  dataCy = 'fixed-table-body-skeleton',
}) => {
  return (
    <tbody
      className="block flex-1 min-h-0 overflow-auto scrollbar-gutter-stable"
      data-cy={dataCy}
      aria-hidden
    >
      {Array.from({length: numberOfRows}, (_, rowIndex) => (
        <tr
          key={`fixed-table-body-skeleton-row-${rowIndex}`}
          className="table table-fixed w-full"
          data-cy={`${dataCy}-row-${rowIndex}`}
        >
          {columns.map((column, index) => {
            const isRightAligned = alignIndexRight.includes(index)

            return (
              <td
                key={`${column.dataKey}-${rowIndex}`}
                className={classNames(
                  'pr-6 py-3 border-b-0.5 border-gray-full/10',
                  {
                    'text-right': isRightAligned,
                  },
                )}
                style={{width: `${column.widthPercentage}%`}}
                data-cy={`${dataCy}-cell-${column.dataKey}-${rowIndex}`}
              >
                <ColumnSkeleton
                  width={index === 0 ? 120 : 64}
                  height={10}
                  classes={isRightAligned ? 'ml-auto' : undefined}
                />
              </td>
            )
          })}
        </tr>
      ))}
    </tbody>
  )
}
